if (!customElements.get('open-box-item')) {
  class OpenBoxItem extends HTMLElement {
    constructor() {
      super();
      this.openBoxVariantId = this.dataset.openBoxVariantId || null;
    }

    connectedCallback() {
      this.card = this.querySelector('.open-box-card');
      this.currentPriceEl = this.querySelector('[data-open-box-price]');
      this.comparePriceEl = this.querySelector('[data-open-box-compare-price]');
      this.savePillEl = this.querySelector('[data-open-box-save-pill]');
      this.savingsTextEl = this.querySelector('[data-open-box-savings-text]');
      this.stockTextEl = this.querySelector('[data-open-box-stock-text]');
      this.buyBtn = this.querySelector('[data-open-box-buy-btn]');
      this.errorEl = this.querySelector('[data-open-box-error]');

      if (this.buyBtn) {
        this.buyBtn.addEventListener('click', this.onBuyOpenBox.bind(this));
      }

      if (this.dataset.openBoxVariantId) {
        this.openBoxVariantId = this.dataset.openBoxVariantId;
        if (this.buyBtn) this.buyBtn.disabled = false;
        return;
      }

      this.findOpenBoxItem();
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && window.theme?.moneyFormat) {
        return Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }

    findMatch(products, rawSku, cleanSku) {
      if (!Array.isArray(products)) return null;

      for (const product of products) {
        if (!product.variants || !Array.isArray(product.variants)) continue;

        for (const variant of product.variants) {
          const vSku = (variant.sku || '').toLowerCase().trim();
          const vSkuClean = vSku.replace(/[^a-z0-9]/g, '');

          if (!vSkuClean) continue;

          if (vSku === rawSku || vSkuClean === cleanSku) continue;

          const isMatch = (
            vSku === `snd${rawSku}` ||
            vSku === `snd-${rawSku}` ||
            vSku === `snd_${rawSku}` ||
            vSku === `${rawSku}-snd` ||
            vSku === `${rawSku}snd` ||
            vSku === `${rawSku}_snd` ||
            vSkuClean === `snd${cleanSku}` ||
            vSkuClean === `${cleanSku}snd` ||
            (vSkuClean.startsWith('snd') && vSkuClean.slice(3) === cleanSku) ||
            (vSkuClean.endsWith('snd') && vSkuClean.slice(0, -3) === cleanSku) ||
            (vSkuClean.includes('snd') && vSkuClean.includes(cleanSku))
          );

          if (isMatch) {
            const isAvailable = variant.available === true;
            const hasStock = variant.inventory_management == null ||
              variant.inventory_policy === 'continue' ||
              (typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity > 0 : true);

            if (isAvailable && hasStock) {
              return { variant, product };
            }
          }
        }
      }
      return null;
    }

    async findOpenBoxItem() {
      const baseSku = (this.dataset.productSku || '').trim();
      if (!baseSku) {
        this.hide();
        return;
      }

      const rawSku = baseSku.toLowerCase();
      const cleanSku = rawSku.replace(/[^a-z0-9]/g, '');

      if (!cleanSku) {
        this.hide();
        return;
      }

      const cacheKey = `openbox_${rawSku}`;
      const CACHE_TTL_MS = 60 * 1000;
      let hasRenderedFromCache = false;

      try {
        const cachedRaw = sessionStorage.getItem(cacheKey);
        if (cachedRaw !== null) {
          const cached = JSON.parse(cachedRaw);
          const isFresh = cached.timestamp && (Date.now() - cached.timestamp < CACHE_TTL_MS);

          if (cached.data && cached.data.variant && cached.data.product) {
            this.renderOpenBox(cached.data.variant, cached.data.product);
            hasRenderedFromCache = true;
            if (isFresh) return;
          } else if (cached.data === null && isFresh) {
            this.hide();
            return;
          }
        }
      } catch (_) { }

      const rootUrl = window.Shopify?.routes?.root || '/';
      const queries = [
        `snd${baseSku}`,
        baseSku
      ];

      try {
        const fetchPromises = queries.map((q) =>
          fetch(`${rootUrl}search?view=openbox&type=product&q=${encodeURIComponent(q)}`, {
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          })
            .then((r) => r.ok ? r.json() : [])
            .catch(() => [])
        );

        const results = await Promise.all(fetchPromises);
        const allProducts = results.flat();
        const matched = this.findMatch(allProducts, rawSku, cleanSku);

        if (matched) {
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ data: matched, timestamp: Date.now() }));
          } catch (_) { }
          this.renderOpenBox(matched.variant, matched.product);
        } else {
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ data: null, timestamp: Date.now() }));
          } catch (_) { }
          this.hide();
        }
      } catch (err) {
        if (!hasRenderedFromCache) {
          console.warn('Open Box lookup error:', err);
          this.hide();
        }
      }
    }

    renderOpenBox(variant, product) {
      this.openBoxVariantId = variant.id;

      const openBoxPrice = variant.price;
      const mainProductPrice = parseInt(this.dataset.productPrice, 10) || openBoxPrice;
      const comparePrice = (variant.compare_at_price && variant.compare_at_price > openBoxPrice)
        ? variant.compare_at_price
        : (mainProductPrice > openBoxPrice ? mainProductPrice : 0);

      if (this.currentPriceEl) {
        this.currentPriceEl.textContent = this.formatMoney(openBoxPrice);
      }

      if (comparePrice > openBoxPrice) {
        const savingsCents = comparePrice - openBoxPrice;
        const savingsPercent = Math.round((savingsCents / comparePrice) * 100);

        if (this.comparePriceEl) {
          this.comparePriceEl.textContent = this.formatMoney(comparePrice);
          this.comparePriceEl.style.display = 'inline';
        }
        if (this.savePillEl) {
          this.savePillEl.textContent = `SAVE ${savingsPercent}%`;
          this.savePillEl.style.display = 'inline-flex';
        }
        if (this.savingsTextEl) {
          this.savingsTextEl.textContent = `You save ${this.formatMoney(savingsCents)} (${savingsPercent}%)`;
          this.savingsTextEl.style.display = 'block';
        }
      } else {
        if (this.comparePriceEl) this.comparePriceEl.style.display = 'none';
        if (this.savePillEl) this.savePillEl.style.display = 'none';
        if (this.savingsTextEl) this.savingsTextEl.style.display = 'none';
      }

      if (this.stockTextEl) {
        if (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity > 0) {
          this.stockTextEl.textContent = `Only ${variant.inventory_quantity} left in stock`;
        } else {
          this.stockTextEl.textContent = 'In stock';
        }
      }

      if (product.url) {
        const productLinks = this.querySelectorAll('[data-open-box-view-link], [data-open-box-link]');
        productLinks.forEach((link) => {
          link.href = product.url;
        });
      }

      if (this.buyBtn) {
        this.buyBtn.disabled = false;
      }

      this.classList.remove('is-hidden');
    }

    hide() {
      this.classList.add('is-hidden');
      this.openBoxVariantId = null;
    }

    async onBuyOpenBox(e) {
      e.preventDefault();
      if (!this.openBoxVariantId) return;

      this.buyBtn.classList.add('is-loading');
      this.buyBtn.disabled = true;
      if (this.errorEl) this.errorEl.style.display = 'none';

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const cartAddUrl = window.routes?.cart_add_url || '/cart/add';

      const formData = new FormData();
      formData.append('id', this.openBoxVariantId);
      formData.append('quantity', 1);

      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
        }
      };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Accept'] = 'application/javascript';
      delete config.headers['Content-Type'];
      config.body = formData;

      try {
        const response = await fetch(cartAddUrl, config);
        const text = await response.text();
        let jsonResponse;
        try {
          jsonResponse = JSON.parse(text);
        } catch (_) {
          jsonResponse = { status: response.status, message: 'Cart Error', description: 'Could not add item to cart.' };
        }

        if (response.ok && !jsonResponse.status) {
          if (cart) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'open-box-item',
                productVariantId: this.openBoxVariantId,
                cartData: jsonResponse
              });
            }
            cart.renderContents(jsonResponse);
            if (typeof cart.open === 'function') {
              cart.open();
            }
          } else {
            window.location = window.routes?.cart_url || '/cart';
          }
        } else {
          const errorMsg = jsonResponse.description || jsonResponse.message || 'Unable to add open box item to cart.';
          if (this.errorEl) {
            this.errorEl.textContent = errorMsg;
            this.errorEl.style.display = 'block';
          }
        }
      } catch (err) {
        console.error('Error adding open box to cart:', err);
        if (this.errorEl) {
          this.errorEl.textContent = 'Error adding item to cart. Please try again.';
          this.errorEl.style.display = 'block';
        }
      } finally {
        this.buyBtn.classList.remove('is-loading');
        this.buyBtn.disabled = false;
      }
    }
  }

  customElements.define('open-box-item', OpenBoxItem);
}
